import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, BRAND } from "../data/content";

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
    }, [open]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "backdrop-blur-xl bg-[#FAF8F5]/85 border-b border-samora-line"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-20 md:h-24 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="brand-logo"
                    className="flex items-baseline gap-2 group"
                    aria-label="Samora Spaces home"
                >
                    <span className="font-display text-2xl md:text-3xl tracking-tight text-samora-ink group-hover:text-samora-gold transition-colors duration-500">
                        Samora
                    </span>
                    <span className="hidden sm:inline font-serif italic text-lg text-samora-gold">
                        Spaces
                    </span>
                </Link>

                <nav
                    data-testid="primary-nav"
                    className="hidden lg:flex items-center gap-10"
                >
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `nav-link ${isActive ? "active" : ""}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <Link
                        to="/contact"
                        data-testid="header-cta-consult"
                        className="btn-samora"
                    >
                        Book Consultation
                    </Link>
                </div>

                <button
                    aria-label="Open menu"
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen(!open)}
                    className="lg:hidden text-samora-ink"
                >
                    {open ? <X size={26} /> : <Menu size={26} />}
                </button>
            </div>

            {/* Mobile drawer */}
            <div
                data-testid="mobile-drawer"
                className={`lg:hidden fixed inset-0 top-20 bg-samora-bone transition-all duration-500 ${
                    open
                        ? "opacity-100 pointer-events-auto"
                        : "opacity-0 pointer-events-none"
                }`}
            >
                <div className="px-8 py-12 flex flex-col gap-8 h-full">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                            onClick={() => setOpen(false)}
                            className={({ isActive }) =>
                                `font-display text-4xl transition-colors ${
                                    isActive
                                        ? "text-samora-gold"
                                        : "text-samora-ink"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <div className="mt-auto pt-8 border-t border-samora-line">
                        <p className="eyebrow mb-2">Studio</p>
                        <p className="text-sm text-samora-text/70 leading-relaxed">
                            {BRAND.address}
                        </p>
                        <a
                            href={`tel:${BRAND.phoneRaw}`}
                            className="mt-4 inline-block text-samora-ink text-lg font-serif"
                        >
                            {BRAND.phone}
                        </a>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
